import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { auth } from '../firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { apiUrl } from '../utils/api';

const AuthContext = createContext(null);

const googleProvider = new GoogleAuthProvider();

function isIosSafari() {
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
  return isIos && isSafari;
}

async function syncUserToServer(firebaseUser) {
  try {
    const token = await firebaseUser.getIdToken();
    const response = await fetch(apiUrl('/api/auth/sync'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('Failed to sync user to server:', error);
  }
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let resolved = false;

    getRedirectResult(auth)
      .then(result => {
        if (result?.user) {
          return syncUserToServer(result.user).then(serverUser => {
            setUser(serverUser || {
              id: result.user.uid,
              email: result.user.email,
              first_name: result.user.displayName?.split(' ')[0] || '',
              last_name: result.user.displayName?.split(' ').slice(1).join(' ') || '',
              profile_image_url: result.user.photoURL,
              displayName: result.user.displayName,
            });
          });
        }
      })
      .catch(err => {
        console.error('Redirect result error:', err);
      });

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!resolved) resolved = true;
      if (firebaseUser) {
        const serverUser = await syncUserToServer(firebaseUser);
        setUser(serverUser || {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          first_name: firebaseUser.displayName?.split(' ')[0] || '',
          last_name: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
          profile_image_url: firebaseUser.photoURL,
          displayName: firebaseUser.displayName,
        });
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loginWithGoogle = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      const { GoogleAuth } = await import('@southdevs/capacitor-google-auth');
      await GoogleAuth.initialize({
        scopes: ['profile', 'email'],
      });
      const googleUser = await GoogleAuth.signIn({
        scopes: ['profile', 'email'],
      });
      const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
      const result = await signInWithCredential(auth, credential);
      return result;
    } else if (isIosSafari()) {
      await signInWithRedirect(auth, googleProvider);
    } else {
      const result = await signInWithPopup(auth, googleProvider);
      return result;
    }
  }, []);

  const loginWithEmail = useCallback(async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signUpWithEmail = useCallback(async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }
  }, []);

  const logout = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const { GoogleAuth } = await import('@southdevs/capacitor-google-auth');
        await GoogleAuth.signOut();
      } catch (e) {}
    }
    await signOut(auth);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      loginWithGoogle,
      loginWithEmail,
      signUpWithEmail,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
